import { useCallback, useMemo } from "react";
import { Button, Checkbox, Disclosure, type Column } from "@legal-500/legal500-design-system";
import { DataTable } from "../../../components/DataTable/DataTable.js";
import type { ApplyStatus, RelabelPreview } from "../../../api/profiles/websiteRelabelService.js";
import type { ManifestRow } from "./parseManifest.js";
import { resolveContent, resolveName, type RowContent } from "./rowDisplay.js";
import styles from "./WebsiteRelabel.module.css";

/**
 * A row's fate as the page knows it. API statuses plus three client-side
 * verdicts: "invalid" for rows that never reach the API, because a single
 * invalid website in the request would 400 the whole batch; "skipped" for rows
 * that validated ok but were deselected before the run; and "unsent" for rows
 * that were selected and still never written, because a chunk failed and the
 * run stopped short of them. The last two must not share a name — one is the
 * reviewer's own decision, the other is work left undone.
 */
export type Verdict = ApplyStatus | "invalid" | "skipped" | "unsent";

export type RelabelEntry = {
  row: ManifestRow;
  verdict: Verdict;
  detail: string | null;
  /** Absent until the API sends one — the table degrades to no name/content columns. */
  preview?: RelabelPreview;
};

export type VerdictFilter = Verdict | "all";

/** Display order: the writable rows first, then the skips, then the breakage. */
export const VERDICT_ORDER: Verdict[] = [
  "ok",
  "unsent",
  "skipped",
  "noop",
  "stale_current",
  "not_found",
  "constraint_violation",
  "group_collision",
  "invalid",
  "failed",
];

/**
 * "ok" reads differently before and after the run: a validate ok is a promise,
 * an apply ok is a fact. Every other status keeps one name throughout.
 */
export function verdictLabel(verdict: Verdict, mode: "validate" | "apply"): string {
  if (verdict === "ok") {
    return mode === "apply" ? "Applied" : "Ok";
  }
  const labels: Record<Exclude<Verdict, "ok">, string> = {
    noop: "No-op",
    stale_current: "Stale current",
    not_found: "Not found",
    constraint_violation: "Constraint violation",
    group_collision: "Group collision",
    invalid: "Invalid row",
    skipped: "Deselected",
    unsent: "Not sent",
    failed: "Failed",
  };
  return labels[verdict];
}

const PILL_CLASSES: Record<Verdict, string> = {
  ok: styles.pillOk as string,
  noop: styles.pillNeutral as string,
  skipped: styles.pillNeutral as string,
  // Warning, not neutral: an unsent row is outstanding work, not a decision.
  unsent: styles.pillWarning as string,
  stale_current: styles.pillError as string,
  not_found: styles.pillError as string,
  constraint_violation: styles.pillError as string,
  group_collision: styles.pillError as string,
  invalid: styles.pillError as string,
  failed: styles.pillError as string,
};

/**
 * Only rows validate blessed can be written, so only those are selectable.
 * Everything else is shown for context and can never be part of a run.
 */
export function isSelectable(entry: RelabelEntry): boolean {
  return entry.verdict === "ok";
}

/**
 * A selectable row the manifest generator flagged as suspicious — the reason
 * the preview exists at all. `content_looks_en` on a row being moved to the
 * French site means the copy may not actually be French.
 */
export function isAnomaly(entry: RelabelEntry): boolean {
  return isSelectable(entry) && entry.row.anomaly.trim() !== "";
}

/**
 * Selection is lifted to the page, which owns the set and hands back the two
 * mutations the table can trigger. Omitted once the run is done: after apply
 * the verdicts are history and there is nothing left to choose.
 */
export type SelectionControls = {
  /** Line numbers of the selected rows. `lineNumber` is unique per manifest row. */
  selected: ReadonlySet<number>;
  onToggleRow: (lineNumber: number) => void;
  onSetRows: (lineNumbers: number[], selected: boolean) => void;
};

type VerdictTableProps = {
  entries: RelabelEntry[];
  filter: VerdictFilter;
  onFilterChange: (filter: VerdictFilter) => void;
  /** Names the "ok" verdict: a promise while previewing, a fact once applied. */
  mode: "validate" | "apply";
  selection?: SelectionControls;
};

type TableRow = {
  id: string;
  lineNumber: number;
  sourcePostId: string;
  table: string;
  profileId: string;
  /** Already reconciled between preview and manifest — see rowDisplay.ts. */
  name: string;
  content: RowContent | null;
  change: string;
  detectedLang: string;
  anomaly: string;
  verdict: Verdict;
  detail: string | null;
  selectable: boolean;
  selected: boolean;
};

/** Renders `com_en/en → fr/fr`, with an em dash standing in for a null language. */
function changeLabel(row: ManifestRow): string {
  const current = `${row.currentWebsite}/${row.currentLanguage ?? "—"}`;
  const target = `${row.targetWebsite}/${row.targetLanguage ?? "—"}`;
  return `${current} → ${target}`;
}

/**
 * The header checkbox, which is tri-state: some-but-not-all selected reads as
 * indeterminate. The design system's Checkbox has no prop for that — it is a
 * DOM property, not an attribute — so it is set through the forwarded ref.
 */
function SelectAllCheckbox({
  checked,
  indeterminate,
  disabled,
  onChange,
}: {
  checked: boolean;
  indeterminate: boolean;
  disabled: boolean;
  onChange: () => void;
}) {
  // A callback ref rather than useEffect: `indeterminate` is a DOM property
  // with no attribute, and React re-runs the callback whenever it changes.
  const setInput = useCallback(
    (input: HTMLInputElement | null) => {
      if (input) {
        input.indeterminate = indeterminate;
      }
    },
    [indeterminate],
  );
  return (
    <Checkbox
      ref={setInput}
      aria-label="Select all shown rows"
      checked={checked}
      disabled={disabled}
      onChange={onChange}
    />
  );
}

export function VerdictTable({
  entries,
  filter,
  onFilterChange,
  mode,
  selection,
}: VerdictTableProps) {
  const counts = useMemo(() => {
    const tally = new Map<Verdict, number>();
    for (const entry of entries) {
      tally.set(entry.verdict, (tally.get(entry.verdict) ?? 0) + 1);
    }
    return tally;
  }, [entries]);

  // The API may not send previews at all (it is a later addition) and older
  // manifests carry no name/excerpt columns either, so a column of nothing but
  // em dashes is a real possibility — both appear only once either source has
  // something to put in them.
  const hasDescriptions = useMemo(
    () => entries.some(entry => entry.preview || entry.row.name || entry.row.excerpt),
    [entries],
  );

  const visible = useMemo(
    () => entries.filter(entry => filter === "all" || entry.verdict === filter),
    [entries, filter],
  );

  const rows = useMemo<TableRow[]>(
    () =>
      visible.map(entry => ({
        id: `${entry.row.lineNumber}-${entry.row.id}`,
        lineNumber: entry.row.lineNumber,
        sourcePostId: entry.row.sourcePostId,
        table: entry.row.table,
        profileId: entry.row.id,
        name: resolveName(entry.row, entry.preview) ?? "",
        content: resolveContent(entry.row, entry.preview),
        change: changeLabel(entry.row),
        detectedLang: entry.row.detectedLang,
        anomaly: entry.row.anomaly,
        verdict: entry.verdict,
        detail: entry.detail,
        selectable: isSelectable(entry),
        selected: selection ? selection.selected.has(entry.row.lineNumber) : false,
      })),
    [visible, selection],
  );

  /** The header checkbox acts on what is on screen, not on the whole file. */
  const selectableVisible = useMemo(() => rows.filter(row => row.selectable), [rows]);
  const selectedVisible = selectableVisible.filter(row => row.selected).length;
  const allVisibleSelected =
    selectableVisible.length > 0 && selectedVisible === selectableVisible.length;

  /**
   * Deselecting anomalies deliberately reaches past the filter: it is the whole
   * point of the tool's review step, and doing it per-filter would leave the
   * reviewer to guess which chips they still had to visit.
   */
  const anomalyEntries = useMemo(() => entries.filter(isAnomaly), [entries]);
  const selectedAnomalies = selection
    ? anomalyEntries.filter(entry => selection.selected.has(entry.row.lineNumber)).length
    : 0;

  const selectedTotal = selection
    ? entries.filter(entry => isSelectable(entry) && selection.selected.has(entry.row.lineNumber))
        .length
    : 0;
  const selectableTotal = entries.filter(isSelectable).length;

  const columns = useMemo<Column<TableRow>[]>(() => {
    const selectColumn: Column<TableRow>[] = selection
      ? [
          {
            header: (
              <SelectAllCheckbox
                checked={allVisibleSelected}
                indeterminate={selectedVisible > 0 && !allVisibleSelected}
                disabled={selectableVisible.length === 0}
                onChange={() =>
                  selection.onSetRows(
                    selectableVisible.map(row => row.lineNumber),
                    !allVisibleSelected,
                  )
                }
              />
            ) as unknown as string,
            accessor: "selected",
            render: (_value, row) =>
              row.selectable ? (
                <Checkbox
                  aria-label={`Apply row ${row.lineNumber}${row.name ? ` — ${row.name}` : ""}`}
                  checked={row.selected}
                  onChange={() => selection.onToggleRow(row.lineNumber)}
                />
              ) : (
                <span className={styles.mono} aria-hidden="true">
                  —
                </span>
              ),
          },
        ]
      : [];

    const descriptionColumns: Column<TableRow>[] = hasDescriptions
      ? [
          {
            header: "Name",
            accessor: "name",
            wrap: true,
            // Plain text, never markup: the name may have come from the
            // uploaded file. React escapes it here by construction.
            render: (_value, row) => <span className={styles.name}>{row.name || "—"}</span>,
          },
          {
            header: "Content",
            accessor: "content",
            // Wrapping is what makes the expanded excerpt readable: without it
            // the cell inherits the table's nowrap and the row runs off-screen.
            wrap: true,
            render: (_value, row) =>
              row.content ? (
                // Plain text, never markup: the excerpt is profile content the
                // page did not author. React escapes it here by construction.
                //
                // The summary names the source — an API field name, or
                // "manifest" for text the uploaded file supplied. The two are
                // not equally current, so the reviewer has to be able to tell.
                //
                // Flagged rows open on first render: an anomaly is the manifest
                // generator saying "read this before you tick it", so hiding the
                // one thing the reviewer has to read behind a chevron just adds a
                // click to every decision that matters. `defaultOpen` is exactly
                // that — a default. React writes `open` on mount and leaves the
                // element alone afterwards, so the reviewer can still collapse a
                // row, and each <details> keeps its own state.
                <Disclosure summary={row.content.label} defaultOpen={row.anomaly.trim() !== ""}>
                  <p className={styles.excerpt}>{row.content.excerpt}</p>
                </Disclosure>
              ) : (
                <span className={styles.mono}>—</span>
              ),
          },
        ]
      : [];

    return [
      ...selectColumn,
      {
        header: "Source post",
        accessor: "sourcePostId",
        render: (_value, row) => <span className={styles.mono}>{row.sourcePostId || "—"}</span>,
      },
      {
        header: "Table",
        accessor: "table",
        render: (_value, row) => <span className={styles.mono}>{row.table}</span>,
      },
      {
        header: "Id",
        accessor: "profileId",
        // The first eight characters are enough to tell rows apart by eye; the
        // full id stays a hover away rather than widening every row.
        render: (_value, row) => (
          <span className={styles.mono} title={row.profileId}>
            {row.profileId.slice(0, 8)}
          </span>
        ),
      },
      ...descriptionColumns,
      {
        header: "Current → Target",
        accessor: "change",
        render: (_value, row) => <span className={styles.mono}>{row.change}</span>,
      },
      {
        header: "Detected lang",
        accessor: "detectedLang",
        render: (_value, row) => <span>{row.detectedLang || "—"}</span>,
      },
      {
        header: "Anomaly",
        accessor: "anomaly",
        render: (_value, row) =>
          row.anomaly ? (
            <span className={`${styles.pill} ${styles.pillWarning}`}>{row.anomaly}</span>
          ) : (
            <span>—</span>
          ),
      },
      {
        header: "Status",
        accessor: "verdict",
        render: (_value, row) => (
          <span
            className={`${styles.pill} ${PILL_CLASSES[row.verdict]}`}
            title={row.detail ?? undefined}
          >
            {verdictLabel(row.verdict, mode)}
          </span>
        ),
      },
    ];
  }, [mode, hasDescriptions, selection, selectableVisible, allVisibleSelected, selectedVisible]);

  // Only verdicts that actually occur get a chip — a fixed set of nine mostly
  // dead chips would bury the two or three that matter for a given file.
  const filters: VerdictFilter[] = [
    "all",
    ...VERDICT_ORDER.filter(verdict => (counts.get(verdict) ?? 0) > 0),
  ];

  return (
    <div className={styles.stack}>
      <div className={styles.toolbar}>
        <div className={styles.chips}>
          {filters.map(option => (
            <button
              key={option}
              type="button"
              className={`${styles.chip} ${filter === option ? styles.chipActive : ""}`}
              aria-pressed={filter === option}
              onClick={() => onFilterChange(option)}
            >
              {option === "all" ? "All" : verdictLabel(option, mode)}{" "}
              {option === "all" ? entries.length : (counts.get(option) ?? 0)}
            </button>
          ))}
        </div>
      </div>

      {selection ? (
        <div className={styles.toolbar}>
          <span className={styles.resultCount} aria-live="polite">
            {selectedTotal} of {selectableTotal} applicable row
            {selectableTotal === 1 ? "" : "s"} selected
          </span>
          {anomalyEntries.length > 0 ? (
            <Button
              variant="outlined"
              size="small"
              disabled={selectedAnomalies === 0}
              onClick={() =>
                selection.onSetRows(
                  anomalyEntries.map(entry => entry.row.lineNumber),
                  false,
                )
              }
            >
              Deselect anomalies ({selectedAnomalies})
            </Button>
          ) : null}
        </div>
      ) : null}

      <DataTable
        data={rows}
        columns={columns}
        emptyMessage="No rows match."
        rowClassName={row => (row.selectable && !row.selected ? (styles.rowDeselected ?? "") : "")}
      />

      <span className={styles.resultCount}>
        Showing {rows.length} of {entries.length}
      </span>
    </div>
  );
}
